import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { useHookTrackWizardStore } from '@/store/hook-track/wizardStore';
import { useHookTrackStore } from '@/store/hook-track/hookTrackStore';
import { useAuth } from '@/hooks/useAuth';
import { HookTrackWizardStepper } from '@/components/hook-track/wizard/HookTrackWizardStepper';
import { HookTrackWizardFooter } from '@/components/hook-track/wizard/HookTrackWizardFooter';
import { VideoMainStep } from '@/components/hook-track/wizard/steps/VideoMainStep';
import { IntrosSelectionStep } from '@/components/hook-track/wizard/steps/IntrosSelectionStep';
import { TrafficDistributionStep } from '@/components/hook-track/wizard/steps/TrafficDistributionStep';
import { TrackingIntegrationStep } from '@/components/hook-track/wizard/steps/TrackingIntegrationStep';
import { TestNameModal } from '@/components/hook-track/wizard/TestNameModal';
import { TrackingWebhooksResultModal } from '@/components/hook-track/wizard/TrackingWebhooksResultModal';
import { createTracking } from '@/services/trackingService';
import { platformsData } from '@/components/tracking/platformsData';
import { useToast } from '@/hooks/use-toast';
import { Lock, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export default function HookTrackCreate() {
  useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, token } = useAuth();
  const {
    currentStep,
    steps,
    setCurrentStep,
    nextStep,
    prevStep,
    validateCurrentStep,
    completeStep,
    data,
    setTestInfo,
    reset
  } = useHookTrackWizardStore();

  const { createTest } = useHookTrackStore();

  const [isFinishing, setIsFinishing] = React.useState(false);
  const [showNameModal, setShowNameModal] = React.useState(!data.testName);
  const [resultWebhooks, setResultWebhooks] = React.useState<Array<{ name: string; url: string }>>([]);
  const [showWebhooksModal, setShowWebhooksModal] = React.useState(false);

  // Forçar re-renderização quando selectedIntros ou trafficDistribution muda
  const canAdvance = React.useMemo(() => {
    if (currentStep === 1) {
      return data.mainVideo !== null;
    } else if (currentStep === 2) {
      return data.selectedIntros.length >= 2;
    } else if (currentStep === 3) {
      // Verificar se há intros selecionadas E se o total é 100 (com arredondamento)
      if (data.selectedIntros.length === 0) return false;
      const total = Object.values(data.trafficDistribution.percentages).reduce((a, b) => a + b, 0);
      return Math.round(total) === 100;
    } else if (currentStep === 4) {
      // Etapa de rastreamento é opcional: válida se desligada, ou com plataforma + token
      const t = data.trackingIntegration;
      return !t.enabled || (t.platformId !== null && t.platformToken.trim().length > 0);
    }
    return false;
  }, [currentStep, data.mainVideo, data.selectedIntros, data.trafficDistribution.percentages, data.trackingIntegration]);

  const handleNext = () => {
    if (validateCurrentStep()) {
      completeStep(currentStep);
      nextStep();
    }
  };

  const handleFinish = async () => {
    if (!validateCurrentStep()) {
      toast({
        title: translateInterfaceText("Configuração incompleta"),
        description: translateInterfaceText("Revise a etapa atual antes de iniciar o teste."),
        variant: "destructive"
      });
      return;
    }

    const userId = user?.id_user || user?.id;
    if (!userId) {
      toast({
        title: translateInterfaceText("Erro de autenticação"),
        description: translateInterfaceText("Você precisa estar logado para criar um teste."),
        variant: "destructive"
      });
      return;
    }

    if (!data.mainVideo) {
      toast({
        title: translateInterfaceText("Vídeo principal não selecionado"),
        description: translateInterfaceText("Por favor, selecione um vídeo principal."),
        variant: "destructive"
      });
      return;
    }

    setIsFinishing(true);

    try {
      // Preparar payload para criação do teste
      const leadVideosIds = data.selectedIntros.map(intro => intro.id);

      const payload = {
        leadTestData: {
          leadTestTitle: data.testName,
          userId: userId,
        },
        leadVideosIds,
      };

      // Criar o teste
      const newTest = await createTest(data.mainVideo.id, payload);

      if (newTest) {
        // Se houver distribuição manual de tráfego, atualizar
        if (data.trafficDistribution.mode === 'manual') {
          // TODO: Implementar atualização de tráfego se necessário
          // const trafficPayload = {
          //   videosIds: data.selectedIntros.map(intro => ({
          //     id_video: intro.id,
          //     trafego: data.trafficDistribution.percentages[intro.id] || 0
          //   }))
          // };
          // await updateTestTraffic(newTest.id, trafficPayload);
        }

        // Integração de rastreamento (opcional): cria uma integração de conversão
        // por intro, todas com a mesma plataforma + token escolhidos no step 4.
        const tracking = data.trackingIntegration;
        const createdWebhooks: Array<{ name: string; url: string }> = [];
        if (tracking.enabled && tracking.platformId && token) {
          const results = await Promise.allSettled(
            data.selectedIntros.map((intro) =>
              createTracking(
                {
                  userId,
                  plataformId: tracking.platformId,
                  videoId: intro.id,
                  plataformToken: tracking.platformToken,
                },
                token
              )
            )
          );

          results.forEach((r, i) => {
            if (r.status === 'fulfilled' && r.value?.webhookUrl) {
              createdWebhooks.push({
                name: data.selectedIntros[i]?.name || `Intro #${i + 1}`,
                url: r.value.webhookUrl,
              });
            }
          });

          const failed = results.length - createdWebhooks.length;
          if (failed > 0) {
            console.error('Falha ao criar integrações de rastreamento:', results);
            toast({
              title: translateInterfaceText("Rastreamento parcial"),
              description: translateInterfaceText("{value0} de {value1} integrações criadas. Você pode configurar as demais em Rastreamento.", { value0: (createdWebhooks.length), value1: (results.length) }),
              variant: "destructive",
            });
          }
        }

        toast({
          title: translateInterfaceText("Teste criado com sucesso!"),
          description: translateInterfaceText("Seu teste de introduções está ativo e coletando dados."),
        });

        // Se há URLs de webhook a configurar, mostra o modal antes de sair;
        // o reset/navegação acontece ao fechar o modal.
        if (createdWebhooks.length > 0) {
          setResultWebhooks(createdWebhooks);
          setShowWebhooksModal(true);
        } else {
          reset();
          navigate('/hook-track/list', { state: { refresh: true } });
        }
      } else {
        throw new Error(translateInterfaceText("Falha ao criar o teste"));
      }
    } catch (error: any) {
      console.error('Erro ao criar teste:', error);
      toast({
        title: translateInterfaceText("Erro ao criar teste"),
        description: error?.message || translateInterfaceText("Ocorreu um erro ao criar o teste. Tente novamente."),
        variant: "destructive"
      });
    } finally {
      setIsFinishing(false);
    }
  };

  const handleExit = () => {
    if (window.confirm(translateInterfaceText("Deseja sair? As alterações não salvas serão perdidas."))) {
      reset();
      navigate('/hook-track/list');
    }
  };

  const handleTestNameConfirm = (name: string) => {
    setTestInfo(name, '');
    setShowNameModal(false);
  };

  const handleTestNameCancel = () => {
    navigate('/hook-track/list');
  };

  const handleWebhooksModalClose = () => {
    setShowWebhooksModal(false);
    reset();
    navigate('/hook-track/list', { state: { refresh: true } });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <VideoMainStep />;
      case 2:
        return <IntrosSelectionStep />;
      case 3:
        return <TrafficDistributionStep />;
      case 4:
        return <TrackingIntegrationStep />;
      default:
        return null;
    }
  };

  return (
    <Layout fixedSidebar>
      <TestNameModal
        open={showNameModal}
        onConfirm={handleTestNameConfirm}
        onCancel={handleTestNameCancel}
      />

      <TrackingWebhooksResultModal
        open={showWebhooksModal}
        webhooks={resultWebhooks}
        platformName={platformsData.find((p) => p.id === data.trackingIntegration.platformSlug)?.name}
        onClose={handleWebhooksModalClose}
      />

      {/* Layout de Modal Full-Screen com áreas fixas */}
      <div className="flex flex-col h-[100dvh] lg:h-screen bg-white overflow-hidden">
        {/* Header de Contexto do Experimento */}
        <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pt-6 pb-4 bg-white">
        {currentStep === 1 ? (
            <>
              <div className="flex items-center gap-2">
                <Lock className="w-[18px] h-[18px] text-[#6B7280]" />
                <h2 className="text-[18px] lg:text-[20px] font-semibold text-[#1A1A1A]">
                  {translateInterfaceText("Definição do Vídeo Base")}</h2>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-4 h-4 text-[#9CA3AF] cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      className="max-w-[280px] bg-[#0A1A3A] text-white border-none text-[13px] p-3"
                    >
                      <p>{translateInterfaceText("O vídeo selecionado, será exibido após todas as introduções testadas.")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </>
          ) : currentStep === 2 ? (
            <>
              <h2 className="text-[18px] lg:text-[20px] font-semibold text-[#1A1A1A]">
                {translateInterfaceText("Selecione os vídeos de teste")}</h2>
            </>
          ) : currentStep === 3 ? (
            <>
              <h2 className="text-[18px] lg:text-[20px] font-semibold text-[#1A1A1A]">
                {translateInterfaceText("Selecione o tráfego dos vídeos")}</h2>
            </>
          ) : currentStep === 4 ? (
            <>
              <h2 className="text-[18px] lg:text-[20px] font-semibold text-[#1A1A1A]">
                {translateInterfaceText("Integração de rastreamento")}</h2>
            </>
          ) : null}
        </div>

        <div className="hidden lg:block h-px w-full mt-[-7px]" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }} />

        {/* Stepper - Abaixo do Header */}
        <div className="flex-shrink-0">
          <HookTrackWizardStepper
            steps={steps}
            currentStep={currentStep}
            onStepClick={setCurrentStep}
          />
        </div>

        {/* Conteúdo - Área principal com scroll interno gerenciado pelo step */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 pb-24">
          <div className="max-w-[1440px] mx-auto">
            {renderStepContent()}
          </div>
        </div>

        {/* Footer - Fixo no rodapé (agora position: fixed) */}
        <HookTrackWizardFooter
            currentStep={currentStep}
            totalSteps={steps.length}
            stepsStartAtOne={true}
            canGoNext={canAdvance}
            onBack={prevStep}
            onNext={handleNext}
            onFinish={handleFinish}
            isFinishing={isFinishing}
            onExit={handleExit}
          />
      </div>
    </Layout>
  );
}
